import React from 'react';

export interface LoadingSpinnerProps {
  size?: 'sm' | 'md' | 'lg';
  color?: 'primary' | 'secondary';
  message?: string;
  layout?: 'vertical' | 'horizontal';
  className?: string;
}

const sizeClasses = {
  sm: 'w-6 h-6',
  md: 'w-12 h-12',
  lg: 'w-16 h-16',
};

// border-width/border-color como estilos inline: el JIT de Tailwind v4 beta no
// genera `border-4`/`border-t-transparent` en este paquete (E45 — mismo problema
// ya documentado para clases arbitrarias, aquí en una utilidad estándar).
const borderWidthPx = {
  sm: 2,
  md: 4,
  lg: 4,
};

const borderColorHex = {
  primary: '#0063EA', // klonup-sky
  secondary: '#6e7883',
};

const layoutClasses = {
  vertical: 'flex-col',
  horizontal: 'flex-row gap-3',
};

const messageLayoutClasses = {
  vertical: 'mt-4',
  horizontal: '',
};

/**
 * LoadingSpinner Component
 *
 * Un spinner de carga consistente para toda la aplicación.
 *
 * @example
 * ```tsx
 * // Layout vertical (default) — spinner + mensaje debajo
 * <LoadingSpinner size="md" message="Cargando datos..." />
 *
 * // Layout horizontal inline — spinner + mensaje al lado
 * <LoadingSpinner size="sm" layout="horizontal" message="Guardando..." />
 * ```
 */
export const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({
  size = 'md',
  color = 'primary',
  message,
  layout = 'vertical',
  className = '',
}) => {
  return (
    <div
      data-testid="loading-spinner-root"
      data-layout={layout}
      className={`flex ${layoutClasses[layout]} items-center justify-center ${className}`}
    >
      <div
        data-testid="loading-spinner"
        className={`${sizeClasses[size]} rounded-full animate-spin`}
        style={{
          borderWidth: `${borderWidthPx[size]}px`,
          borderStyle: 'solid',
          borderColor: borderColorHex[color],
          borderTopColor: 'transparent',
        }}
      />
      {message && (
        <p
          data-testid="loading-spinner-message"
          className={`text-[#6e7883] ${messageLayoutClasses[layout]} text-sm`}
        >
          {message}
        </p>
      )}
    </div>
  );
};
